/**
 * Painel do Escritório — o dashboard.
 *
 * O desk antigo abria num dashboard; este é o equivalente com SQL de verdade
 * por trás. Um seletor de período (30/90/365 dias) refaz tudo numa chamada.
 *
 * Decisões de leitura (não de gosto):
 *   - Os gráficos de movimento contam MOVIMENTOS, não somam quantidades —
 *     somar sacos com metros e latas daria um número sem significado.
 *   - Duas séries no tempo = duas cores fixas da família da marca, validadas
 *     para daltonismo e contraste (azul #2273c2 entradas, dourado #b8860b
 *     saídas) — a cor segue a série, nunca a posição.
 *   - Barras horizontais para "por obra" e "por item": nomes longos leem-se
 *     melhor na horizontal, e o valor vai direto na ponta da barra.
 *   - Sem eixo duplo, sem pizza, sem cor decorativa.
 */

import { api, ErroApi } from '../api.js';
import { escapar, numero } from '../comum/formato.js';
import { VERSAO_ATUAL } from '../versoes.js';
import { abrirHistoricoDeVersoes } from '../comum/historico-versoes.js';

interface Painel {
  dias: number;
  agrupamento: 'dia' | 'semana';
  kpis: {
    itensEmUso: number;
    saldoNegativo: number;
    aprovacoesPendentes: number;
    pedidosNaFila: number;
    romaneiosAbertos: number;
    lotesVencidos: number;
  };
  movimento: { balde: string; entradas: number; saidas: number }[];
  topObras: { obra: string; obraNome: string; saidas: number; romaneios: number }[];
  topItens: {
    codigo: string;
    nome: string;
    unidade: string | null;
    saldo: number;
    saidas: number;
    miniatura: string | null;
  }[];
  pedidosPorStatus: { status: string; n: number }[];
}

/** As duas cores de série — família da marca, validadas (ver cabeçalho). */
const COR = { entradas: '#2273c2', saidas: '#b8860b' };

const STATUS_PEDIDO: Record<string, string> = {
  aguardando_analise: 'Aguardando análise',
  em_analise: 'Em análise',
  separado_parcial: 'Parcial — com o solicitante',
  separado_integral: 'Separado, com romaneio',
  aceito: 'Parcial aceito, com romaneio',
  recusado: 'Recusados',
  cancelado: 'Cancelados',
};

export function montarPainel(raiz: HTMLElement, aoNavegar?: (chave: string) => void): void {
  let dias = 30;
  let series: 'ambas' | 'entradas' | 'saidas' = 'ambas';
  let dados: Painel | undefined;

  raiz.innerHTML = `
    <div class="pn">
      <div class="chips" id="pn-periodo">
        ${[30, 90, 365]
          .map(
            (d) =>
              `<button class="chip" data-dias="${d}" aria-pressed="${d === dias}">${
                d === 365 ? '12 meses' : `${d} dias`
              }</button>`,
          )
          .join('')}
      </div>
      <div id="pn-corpo"><p class="carregando">Montando o painel…</p></div>
    </div>`;

  const corpo = raiz.querySelector<HTMLElement>('#pn-corpo')!;

  raiz.querySelector('#pn-periodo')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-dias]');
    if (!b) return;
    dias = Number(b.dataset['dias']);
    raiz
      .querySelectorAll('#pn-periodo .chip')
      .forEach((c) => c.setAttribute('aria-pressed', String(c === b)));
    void carregar();
  });

  async function carregar(): Promise<void> {
    corpo.innerHTML = `<p class="carregando">Montando o painel…</p>`;
    try {
      dados = await api.get<Painel>(`/api/painel?dias=${dias}`);
      desenhar();
    } catch (erro) {
      corpo.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui montar o painel.',
      )}</p>`;
    }
  }

  /**
   * O aviso de NOVA VERSÃO (pedido do Lucas, 10/08): quando produção atualiza
   * (sempre em lote, a pedido dele), o Painel conta O QUE MUDOU em linguagem
   * de usuário. Cada pessoa dispensa uma vez por versão — guardado no
   * navegador dela.
   */
  function novidadesHtml(): string {
    if (localStorage.getItem('novidades-vistas') === VERSAO_ATUAL.versao) return '';
    // RESUMO aqui, o resto atrás do link (14/08, Lucas) — o aviso não pode
    // virar uma parede de texto a cada atualização.
    const resumo = VERSAO_ATUAL.itens.slice(0, 3);
    return `
      <section class="pn-novidades">
        <div class="pn-nv-topo">
          <h3>🆕 Novidades da versão ${escapar(VERSAO_ATUAL.versao)}</h3>
          <button class="btn btn-claro" id="pn-nv-ok">Entendi</button>
        </div>
        <ul>${resumo.map((i) => `<li>${escapar(i)}</li>`).join('')}</ul>
        <p class="pn-nv-mais">
          ${VERSAO_ATUAL.itens.length > resumo.length ? `e mais ${VERSAO_ATUAL.itens.length - resumo.length} mudança(s). ` : ''}
          <a href="#" data-ver-historico>Ver histórico de atualizações →</a>
        </p>
      </section>`;
  }

  function desenhar(): void {
    const d = dados!;
    corpo.innerHTML = `
      ${novidadesHtml()}
      ${kpis(d)}
      <section class="ped-painel">
        <div class="pn-cab">
          <h3>Movimentação do estoque <span class="sub-inline">nº de movimentos por ${d.agrupamento}</span></h3>
          <div class="chips pn-series">
            ${(['ambas', 'entradas', 'saidas'] as const)
              .map(
                (s) =>
                  `<button class="chip" data-serie="${s}" aria-pressed="${s === series}">${
                    s === 'ambas' ? 'Ambas' : s === 'entradas' ? 'Entradas' : 'Saídas'
                  }</button>`,
              )
              .join('')}
          </div>
        </div>
        <div class="pn-legenda">
          ${series !== 'saidas' ? `<span><i style="background:${COR.entradas}"></i> Entradas</span>` : ''}
          ${series !== 'entradas' ? `<span><i style="background:${COR.saidas}"></i> Saídas</span>` : ''}
        </div>
        <div class="pn-grafico" id="pn-tempo"></div>
        <details class="pn-numeros"><summary>ver os números</summary>${tabelaMovimento(d)}</details>
      </section>

      <div class="pn-2col">
        <section class="ped-painel">
          <h3>Para onde o material foi <span class="sub-inline">saídas por obra</span></h3>
          ${barrasObras(d)}
        </section>
        <section class="ped-painel">
          <h3>O que mais sai do galpão <span class="sub-inline">nº de saídas</span></h3>
          ${barrasItens(d)}
        </section>
      </div>

      <section class="ped-painel">
        <h3>Pedidos no funil <span class="sub-inline">situação atual, todos os tempos</span></h3>
        ${barrasPedidos(d)}
      </section>`;

    corpo.querySelector('#pn-nv-ok')?.addEventListener('click', () => {
      localStorage.setItem('novidades-vistas', VERSAO_ATUAL.versao);
      corpo.querySelector('.pn-novidades')?.remove();
    });
    corpo.querySelector('[data-ver-historico]')?.addEventListener('click', (e) => {
      e.preventDefault();
      abrirHistoricoDeVersoes();
    });

    corpo.querySelector('.pn-series')!.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-serie]');
      if (!b) return;
      series = b.dataset['serie'] as typeof series;
      desenhar();
    });
    desenharTempo(d);
    corpo.querySelectorAll<HTMLElement>('[data-ir]').forEach((el) => {
      el.addEventListener('click', () => aoNavegar?.(el.dataset['ir']!));
    });
  }

  // ------------------------------------------------------------------- KPIs

  function kpis(d: Painel): string {
    const t = (
      valor: number,
      rotulo: string,
      ir?: string,
      alerta = false,
    ) => `
      <${ir ? `button data-ir="${ir}"` : 'div'} class="pn-kpi ${alerta && valor > 0 ? 'alerta' : ''}">
        <b>${numero(valor)}</b><span>${rotulo}</span>
      </${ir ? 'button' : 'div'}>`;
    return `
      <div class="pn-kpis">
        ${t(d.kpis.itensEmUso, 'itens em uso', 'estoque')}
        ${t(d.kpis.aprovacoesPendentes, 'aprovações pendentes', 'aprovacoes', true)}
        ${t(d.kpis.pedidosNaFila, 'pedidos na fila', 'pedidos', true)}
        ${t(d.kpis.romaneiosAbertos, 'romaneios na rua', 'romaneios')}
        ${t(d.kpis.lotesVencidos, 'lotes vencidos', 'validades', true)}
        ${t(d.kpis.saldoNegativo, 'saldos negativos', 'estoque', true)}
      </div>`;
  }

  // ------------------------------------------- linha do tempo (SVG + cursor)

  interface Ponto {
    rotulo: string;
    entradas: number;
    saidas: number;
  }

  /** Preenche os buracos: dia sem movimento é zero, não ausência. */
  function eixoCompleto(d: Painel): Ponto[] {
    const por = new Map(d.movimento.map((m) => [m.balde, m]));
    const passo = d.agrupamento === 'semana' ? 7 : 1;
    const pontos: Ponto[] = [];
    const fim = new Date();
    const inicio = new Date(fim.getTime() - d.dias * 86_400_000);
    // Alinha ao começo da semana (segunda) quando agrupado por semana.
    if (d.agrupamento === 'semana') {
      inicio.setDate(inicio.getDate() - ((inicio.getDay() + 6) % 7));
    }
    for (let t = inicio.getTime(); t <= fim.getTime(); t += passo * 86_400_000) {
      const chave = new Date(t).toISOString().slice(0, 10);
      const m = por.get(chave);
      pontos.push({ rotulo: chave, entradas: m?.entradas ?? 0, saidas: m?.saidas ?? 0 });
    }
    return pontos;
  }

  function desenharTempo(d: Painel): void {
    const caixa = corpo.querySelector<HTMLElement>('#pn-tempo')!;
    const pontos = eixoCompleto(d);
    if (pontos.every((p) => p.entradas === 0 && p.saidas === 0)) {
      caixa.innerHTML = `<p class="dica">Nenhum movimento no período.</p>`;
      return;
    }

    const L = 760;
    const A = 220;
    const M = { esq: 34, dir: 10, topo: 10, base: 24 };
    const teto = Math.max(
      ...pontos.map((p) =>
        Math.max(series !== 'saidas' ? p.entradas : 0, series !== 'entradas' ? p.saidas : 0),
      ),
      4,
    );
    const x = (i: number) => M.esq + (i / Math.max(pontos.length - 1, 1)) * (L - M.esq - M.dir);
    const y = (v: number) => M.topo + (1 - v / teto) * (A - M.topo - M.base);

    const linha = (chave: 'entradas' | 'saidas') =>
      pontos.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[chave]).toFixed(1)}`).join('');

    // Grade discreta: 3 linhas horizontais, valores à esquerda.
    const grades = [0, 0.5, 1]
      .map((f) => {
        const v = Math.round(teto * f);
        return `<line x1="${M.esq}" x2="${L - M.dir}" y1="${y(v)}" y2="${y(v)}" class="pn-grade"/>
                <text x="${M.esq - 6}" y="${y(v) + 3.5}" class="pn-eixo" text-anchor="end">${v}</text>`;
      })
      .join('');

    // Rótulos de x esparsos: ~6, nunca um por ponto.
    const cada = Math.max(1, Math.ceil(pontos.length / 6));
    const rotulosX = pontos
      .map((p, i) =>
        i % cada === 0
          ? `<text x="${x(i)}" y="${A - 6}" class="pn-eixo" text-anchor="middle">${p.rotulo.slice(8, 10)}/${p.rotulo.slice(5, 7)}</text>`
          : '',
      )
      .join('');

    caixa.innerHTML = `
      <svg viewBox="0 0 ${L} ${A}" class="pn-svg" role="img"
           aria-label="Movimentos por ${d.agrupamento}: entradas e saídas">
        ${grades}${rotulosX}
        ${series !== 'saidas' ? `<path d="${linha('entradas')}" class="pn-serie" stroke="${COR.entradas}"/>` : ''}
        ${series !== 'entradas' ? `<path d="${linha('saidas')}" class="pn-serie" stroke="${COR.saidas}"/>` : ''}
        <line id="pn-cursor" y1="${M.topo}" y2="${A - M.base}" class="pn-cursor" visibility="hidden"/>
        ${series !== 'saidas' ? `<circle id="pn-pe" r="4" fill="${COR.entradas}" class="pn-ponto" visibility="hidden"/>` : ''}
        ${series !== 'entradas' ? `<circle id="pn-ps" r="4" fill="${COR.saidas}" class="pn-ponto" visibility="hidden"/>` : ''}
      </svg>
      <div class="pn-dica" id="pn-tip" hidden></div>`;

    // O cursor: aproximar mostra o ponto mais próximo, com os dois valores.
    const svg = caixa.querySelector<SVGSVGElement>('svg')!;
    const tip = caixa.querySelector<HTMLElement>('#pn-tip')!;
    const cursor = svg.querySelector<SVGLineElement>('#pn-cursor')!;
    const pe = svg.querySelector<SVGCircleElement>('#pn-pe');
    const ps = svg.querySelector<SVGCircleElement>('#pn-ps');

    svg.addEventListener('mousemove', (e) => {
      const r = svg.getBoundingClientRect();
      const fx = ((e.clientX - r.left) / r.width) * L;
      const i = Math.round(((fx - M.esq) / (L - M.esq - M.dir)) * (pontos.length - 1));
      if (i < 0 || i >= pontos.length) return;
      const p = pontos[i]!;
      cursor.setAttribute('x1', String(x(i)));
      cursor.setAttribute('x2', String(x(i)));
      cursor.setAttribute('visibility', 'visible');
      if (pe) { pe.setAttribute('cx', String(x(i))); pe.setAttribute('cy', String(y(p.entradas))); pe.setAttribute('visibility', 'visible'); }
      if (ps) { ps.setAttribute('cx', String(x(i))); ps.setAttribute('cy', String(y(p.saidas))); ps.setAttribute('visibility', 'visible'); }
      const [a, m, di] = [p.rotulo.slice(0, 4), p.rotulo.slice(5, 7), p.rotulo.slice(8, 10)];
      tip.innerHTML = `<b>${d.agrupamento === 'semana' ? 'semana de ' : ''}${di}/${m}/${a}</b>
        ${series !== 'saidas' ? `<span><i style="background:${COR.entradas}"></i> ${p.entradas} entrada(s)</span>` : ''}
        ${series !== 'entradas' ? `<span><i style="background:${COR.saidas}"></i> ${p.saidas} saída(s)</span>` : ''}`;
      tip.hidden = false;
      const lado = fx > L / 2;
      tip.style.left = lado ? '' : `${((x(i) / L) * 100).toFixed(1)}%`;
      tip.style.right = lado ? `${(100 - (x(i) / L) * 100).toFixed(1)}%` : '';
    });
    svg.addEventListener('mouseleave', () => {
      tip.hidden = true;
      cursor.setAttribute('visibility', 'hidden');
      pe?.setAttribute('visibility', 'hidden');
      ps?.setAttribute('visibility', 'hidden');
    });
  }

  function tabelaMovimento(d: Painel): string {
    return `
      <div class="rolagem"><table class="tabela">
        <thead><tr><th>${d.agrupamento === 'semana' ? 'Semana de' : 'Dia'}</th>
          <th class="num">Entradas</th><th class="num">Saídas</th></tr></thead>
        <tbody>${d.movimento
          .map(
            (m) => `<tr><td>${m.balde.slice(8, 10)}/${m.balde.slice(5, 7)}/${m.balde.slice(0, 4)}</td>
              <td class="num">${m.entradas}</td><td class="num">${m.saidas}</td></tr>`,
          )
          .join('')}</tbody>
      </table></div>`;
  }

  // --------------------------------------------------- barras horizontais

  function barra(fracao: number, cor: string): string {
    return `<span class="pn-barra"><i style="width:${(fracao * 100).toFixed(1)}%;background:${cor}"></i></span>`;
  }

  function barrasObras(d: Painel): string {
    if (d.topObras.length === 0) return `<p class="dica">Nenhuma saída para obra no período.</p>`;
    const teto = d.topObras[0]!.saidas;
    return `<div class="pn-barras">${d.topObras
      .map(
        (o) => `
      <div class="pn-blinha" title="${escapar(o.obraNome)}: ${o.saidas} saída(s) em ${o.romaneios} romaneio(s)">
        <span class="pn-brotulo">${escapar(o.obraNome)}</span>
        ${barra(o.saidas / teto, COR.entradas)}
        <span class="pn-bvalor">${o.saidas}</span>
      </div>`,
      )
      .join('')}</div>`;
  }

  function barrasItens(d: Painel): string {
    if (d.topItens.length === 0) return `<p class="dica">Nenhuma saída de item no período.</p>`;
    const teto = d.topItens[0]!.saidas;
    return `<div class="pn-barras">${d.topItens
      .map(
        (i) => `
      <div class="pn-blinha" title="${escapar(i.nome)}: ${i.saidas} saída(s) · saldo atual ${numero(i.saldo)} ${escapar(i.unidade ?? '')}">
        <span class="pn-bfoto">${i.miniatura ? `<img src="${i.miniatura}" alt="">` : ''}</span>
        <span class="pn-brotulo">${escapar(i.nome)}</span>
        ${barra(i.saidas / teto, COR.entradas)}
        <span class="pn-bvalor">${i.saidas}</span>
      </div>`,
      )
      .join('')}</div>`;
  }

  function barrasPedidos(d: Painel): string {
    const ordem = Object.keys(STATUS_PEDIDO);
    const quais = d.pedidosPorStatus
      .filter((p) => p.n > 0)
      .sort((a, b) => ordem.indexOf(a.status) - ordem.indexOf(b.status));
    if (quais.length === 0) return `<p class="dica">Nenhum pedido ainda.</p>`;
    const teto = Math.max(...quais.map((p) => p.n));
    return `<div class="pn-barras">${quais
      .map(
        (p) => `
      <div class="pn-blinha">
        <span class="pn-brotulo">${escapar(STATUS_PEDIDO[p.status] ?? p.status)}</span>
        ${barra(p.n / teto, COR.saidas)}
        <span class="pn-bvalor">${p.n}</span>
      </div>`,
      )
      .join('')}</div>`;
  }

  void carregar();
}
